import { canDriveVehicle, generateDate, isEmployed, isStudent } from '@acorn-portal/shared/src/utils';
import { convertToJsDate } from '@acorn-portal/shared/src/utils/formDateValueUtils';
import { COMMON_LAW_PARTNER_VALUES, MIN_AGE } from '@acorn-portal/shared/src/utils/constants';
import { ADDITIONAL_DRIVER_ERRORS, TAXI_ERRORS } from '@acorn-portal/shared/src/utils/constants/ErrorMessages';
import * as Yup from 'yup';
import { validateDateOrder } from './helpers/validate-date-order';
import { differenceInYears, sub } from 'date-fns';
import { validateDrivingLicence, DLNValidationResult } from '@acorn/core';
import posthog from 'posthog-js';
import { ILicenceField } from '@acorn-portal/shared/src/components/inputs/DriverLicenceField';

/**
 * Tests the validity of a driving licence number (DLN).
 * Surname matching is intentionally softened (`validateSurname: false`).
 */
export const testDln = (dln: ILicenceField, lastName: string, dateOfBirth: Date, title: string, firstName: string) => {
	if (!dln['licenceField-1'] || !dln['licenceField-2'] || !dln['licenceField-3'] || !dln['licenceField-4']) {
		return false;
	}
	const DLN = `${dln['licenceField-1']}${dln['licenceField-2']}${dln['licenceField-3']}${dln['licenceField-4']}`;
	const { isValid, errors }: DLNValidationResult = validateDrivingLicence(
		DLN,
		{ lastName, dateOfBirth, title, firstName },
		{ validateSurname: false },
	);

	if (!isValid && errors?.length) posthog.capture('dln_validation_error', { errors });
	return isValid;
};

export const driverSchema = (proposerMaritalStatus: string) =>
	Yup.object().shape({
		title: Yup.string().required(ADDITIONAL_DRIVER_ERRORS.title),
		firstName: Yup.string().required(ADDITIONAL_DRIVER_ERRORS.firstName),
		lastNames: Yup.string().required(ADDITIONAL_DRIVER_ERRORS.lastName),
		dateOfBirth: Yup.object()
			.shape({
				day: Yup.string().required(ADDITIONAL_DRIVER_ERRORS.dateOfBirth),
				month: Yup.string().required(ADDITIONAL_DRIVER_ERRORS.dateOfBirth),
				year: Yup.string().required(ADDITIONAL_DRIVER_ERRORS.dateOfBirth),
			})
			.test('is-min-age', ADDITIONAL_DRIVER_ERRORS.minimumAge, (value, context) => {
				if (!value) return true;
				const minimumDate = sub(new Date(), { years: MIN_AGE });
				const formattedDateOfBirth = generateDate(value);
				const difference = differenceInYears(minimumDate, formattedDateOfBirth);
				const overMinimumAge = difference > 0;
				return overMinimumAge
					? true
					: context.createError({
							path: 'dateOfBirth.year',
							message: ADDITIONAL_DRIVER_ERRORS.minimumAge,
						});
			}),
		maritalStatus: Yup.string().required(ADDITIONAL_DRIVER_ERRORS.maritalStatus),
		relationshipToProposer: Yup.string()
			.test('test-relationship-status', 'error', (value, context) => {
				if (value === 'S' && context.parent.maritalStatus !== 'M') {
					return context.createError({
						path: 'relationshipToProposer',
						message: ADDITIONAL_DRIVER_ERRORS.relationshipToProposerConflict,
					});
				}
				if (value === 'S' && proposerMaritalStatus !== 'M') {
					return context.createError({
						path: 'relationshipToProposer',
						message: ADDITIONAL_DRIVER_ERRORS.relationshipToProposerConflict,
					});
				}
				if (value === 'W') {
					if (!COMMON_LAW_PARTNER_VALUES.includes(proposerMaritalStatus)) {
						return context.createError({
							path: 'relationshipToProposer',
							message: ADDITIONAL_DRIVER_ERRORS.relationshipToProposerConflict,
						});
					}
					if (!COMMON_LAW_PARTNER_VALUES.includes(context.parent.maritalStatus as string)) {
						return context.createError({
							path: 'relationshipToProposer',
							message: ADDITIONAL_DRIVER_ERRORS.relationshipToProposerConflict,
						});
					}
				}
				return true;
			})
			.required(ADDITIONAL_DRIVER_ERRORS.relationshipToProposer),
		isResidentFromBirth: Yup.string().required(ADDITIONAL_DRIVER_ERRORS.isResidentFromBirth),
		ukResidenceSince: Yup.mixed().when('isResidentFromBirth', {
			is: 'no',
			then: () =>
				Yup.object()
					.shape({
						day: Yup.string().required(ADDITIONAL_DRIVER_ERRORS.ukResidenceSince),
						month: Yup.string().required(ADDITIONAL_DRIVER_ERRORS.ukResidenceSince),
						year: Yup.string().required(ADDITIONAL_DRIVER_ERRORS.ukResidenceSince),
					})
					.test((value, context) => {
						const { dateOfBirth } = context.parent;
						if (!dateOfBirth || !value) return true;
						const result = validateDateOrder(value, dateOfBirth);
						if (!result.pass) {
							return context.createError({
								path: 'ukResidenceSince.year',
								message: result.message,
							});
						}
						return true;
					}),
			otherwise: (schema) => schema.notRequired(),
		}),
		employmentType: Yup.string().required(ADDITIONAL_DRIVER_ERRORS.employmentType),
		occupation: Yup.string().test({
			name: 'conditional-occupation',
			test: (value, context) => {
				const { employmentType } = context.parent;
				if (!employmentType || (!isEmployed(employmentType) && !isStudent(employmentType))) return true;

				if (!value) {
					if (isEmployed(employmentType)) {
						return context.createError({
							path: 'occupation',
							message: ADDITIONAL_DRIVER_ERRORS.occupation,
						});
					}

					if (isStudent(employmentType)) {
						return context.createError({
							path: 'occupation',
							message: ADDITIONAL_DRIVER_ERRORS.studentOccupation,
						});
					}
				}

				return true;
			},
		}),
		industry: Yup.mixed().when('employmentType', {
			is: isEmployed,
			then: () => Yup.string().required(ADDITIONAL_DRIVER_ERRORS.industry),
			otherwise: (schema) => schema.notRequired(),
		}),
		otherEmployment: Yup.string().required(ADDITIONAL_DRIVER_ERRORS.otherEmployment),
		employmentStatus: Yup.string().required(ADDITIONAL_DRIVER_ERRORS.otherEmploymentStatus),
		otherOccupation: Yup.string().test({
			name: 'conditional-other-occupation',
			test: (value, context) => {
				const { otherEmployment, otherEmploymentType } = context.parent;
				if (otherEmployment !== 'yes' || (!isEmployed(otherEmploymentType) && !isStudent(otherEmploymentType))) {
					return true;
				}

				if (!value) {
					if (isEmployed(otherEmploymentType)) {
						return context.createError({
							path: 'otherOccupation',
							message: ADDITIONAL_DRIVER_ERRORS.otherOccupation,
						});
					}

					if (isStudent(otherEmploymentType)) {
						return context.createError({
							path: 'otherOccupation',
							message: ADDITIONAL_DRIVER_ERRORS.studentOccupation,
						});
					}
				}
				return true;
			},
		}),
		otherIndustry: Yup.mixed().when(['otherEmployment', 'otherEmploymentType'], {
			is: (otherEmployment: string, otherEmploymentType: string) =>
				otherEmployment === 'yes' && isEmployed(otherEmploymentType),
			then: () => Yup.string().required(ADDITIONAL_DRIVER_ERRORS.otherIndustry),
			otherwise: (schema) => schema.notRequired(),
		}),
		otherEmploymentType: Yup.mixed().when('otherEmployment', {
			is: 'yes',
			then: () => Yup.string().required(ADDITIONAL_DRIVER_ERRORS.otherEmploymentType),
			otherwise: (schema) => schema.notRequired(),
		}),
		otherEmploymentStatus: Yup.string().when('otherEmployment', {
			is: 'yes',
			then: () => Yup.string().required(ADDITIONAL_DRIVER_ERRORS.otherEmploymentStatus),
			otherwise: (schema) => schema.notRequired(),
		}),
		licenceType: Yup.string().required(ADDITIONAL_DRIVER_ERRORS.licenceType),
		licenceObtained: Yup.object()
			.shape({
				day: Yup.string().required(ADDITIONAL_DRIVER_ERRORS.licenceObtained),
				month: Yup.string().required(ADDITIONAL_DRIVER_ERRORS.licenceObtained),
				year: Yup.string().required(ADDITIONAL_DRIVER_ERRORS.licenceObtained),
			})
			.test('licence-after-dob', 'error', (value, context) => {
				const { dateOfBirth } = context.parent;
				if (!dateOfBirth || !value) return true;
				const result = validateDateOrder(value, dateOfBirth);
				if (!result.pass) {
					return context.createError({
						path: 'licenceObtained.year',
						message: result.message,
					});
				}
				return true;
			}),
		hasMotoringConvictions: Yup.string().required(ADDITIONAL_DRIVER_ERRORS.hasMotoringConvictions),
		motoringConvictions: Yup.mixed().when('hasMotoringConvictions', {
			is: 'yes',
			then: () =>
				Yup.array()
					.min(1, ADDITIONAL_DRIVER_ERRORS.motoringConvictionsMin)
					.required(ADDITIONAL_DRIVER_ERRORS.motoringConvictionsMin),
			otherwise: (schema) => schema.notRequired(),
		}),
		hasMotoringClaims: Yup.string().required(ADDITIONAL_DRIVER_ERRORS.hasMotoringClaims),
		motoringClaims: Yup.mixed().when('hasMotoringClaims', {
			is: 'yes',
			then: () =>
				Yup.array().min(1, ADDITIONAL_DRIVER_ERRORS.motoringClaims).required(ADDITIONAL_DRIVER_ERRORS.motoringClaims),
			otherwise: (schema) => schema.notRequired(),
		}),
		hasCriminalConvictions: Yup.string().required(ADDITIONAL_DRIVER_ERRORS.hasCriminalConvictions),
		hasBeenRefusedCover: Yup.string().required(ADDITIONAL_DRIVER_ERRORS.hasBeenRefusedCover),
		licenceNumber: Yup.mixed().when('licenceType', {
			is: (value: string) => value === 'P' || value === 'F',
			then: () =>
				Yup.object()
					.shape({
						'licenceField-1': Yup.string().required(ADDITIONAL_DRIVER_ERRORS.dlnRequired),
						'licenceField-2': Yup.string().required(ADDITIONAL_DRIVER_ERRORS.dlnRequired),
						'licenceField-3': Yup.string().required(ADDITIONAL_DRIVER_ERRORS.dlnRequired),
						'licenceField-4': Yup.string().required(ADDITIONAL_DRIVER_ERRORS.dlnRequired),
					})
					.test('test-licence', ADDITIONAL_DRIVER_ERRORS.dlnValid, (value, context) => {
						return testDln(
							value as ILicenceField,
							context.parent.lastNames,
							convertToJsDate(context.parent.dateOfBirth),
							context.parent.title,
							context.parent.firstName,
						);
					}),
			otherwise: (schema) => schema.notRequired(),
		}),
	});

export const taxiDriverSchema = (proposerMaritalStatus: string, vehicleUsage: string) =>
	driverSchema(proposerMaritalStatus).shape({
		driverUsage: Yup.string()
			.required(ADDITIONAL_DRIVER_ERRORS.driverUsageRequired)
			.test('test-driver-usage', TAXI_ERRORS.publicUsage, (value) => {
				if (!canDriveVehicle(value, vehicleUsage)) {
					return false;
				}
				return true;
			}),
		badgePassedDate: Yup.object().when([], {
			is: () => vehicleUsage === '15' || vehicleUsage === '17',
			then: () =>
				Yup.object()
					.shape({
						day: Yup.string().required(TAXI_ERRORS.badgeObtainedAdditional),
						month: Yup.string().required(TAXI_ERRORS.badgeObtainedAdditional),
						year: Yup.string().required(TAXI_ERRORS.badgeObtainedAdditional),
					})
					.test('badge-date-after-dob', 'error', (value, context) => {
						const { dateOfBirth } = context.parent;
						if (!dateOfBirth || !value) return true;
						const result = validateDateOrder(value, dateOfBirth);
						if (!result.pass) {
							return context.createError({
								path: 'badgePassedDate.year',
								message: result.message,
							});
						}
						return true;
					}),
			otherwise: () =>
				Yup.object().shape({
					day: Yup.string().notRequired(),
					month: Yup.string().notRequired(),
					year: Yup.string().notRequired(),
				}),
		}),
	});
